import { ChevronDown } from "lucide-react";
import { useState } from "react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

/**
 * Controls share Button's `md` height, so a filter row of mixed controls lines up
 * without any one of them being nudged — this is what retired the `h-[34px]` on
 * Combobox. Textarea opts out, because it grows.
 */
const BASE =
  "w-full rounded-md border border-bank-border-strong bg-bank-surface px-2.5 text-sm " +
  "placeholder:text-bank-muted focus-visible:border-bank-accent focus-visible:ring-2 " +
  "focus-visible:ring-bank-accent focus-visible:outline-none";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(BASE, "h-8", className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(BASE, "min-h-20 resize-y py-1.5", className)} {...props} />;
}

/**
 * Two label roles, because this app's labels are two different things.
 *
 * `Label` carries prose — Probe's label is two sentences explaining that a probe
 * spends two model calls, and Ingest's names the accepted extensions. Sentence case,
 * because uppercase mono turns a sentence into a ransom note.
 *
 * `FieldLabel` names a single value: a filter, a metadata key. Those read as data,
 * so they get the mono treatment the rest of the data has.
 */
export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="text-bank-muted mb-1 block text-xs">
      {children}
    </label>
  );
}

export function FieldLabel({
  children,
  htmlFor,
  className,
}: {
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        "text-bank-muted mb-1 block font-mono text-[10.5px] tracking-[0.07em] uppercase",
        className,
      )}
    >
      {children}
    </label>
  );
}

export interface ComboboxOption {
  value: string;
  label: string;
}

/**
 * A filterable dropdown, not a native `<select>`: DESIGN facet vocabularies (stack,
 * concept) run into the hundreds, and scanning that whole list is slower than typing
 * three letters. The caller supplies the full option set — including any "clear the
 * filter" entry, e.g. `{ value: "", label: "any" }` — this component has no built-in
 * opinion on what an empty value means.
 *
 * `allowCustomValue` (default false) commits whatever was typed even when nothing in
 * `options` matches it, instead of discarding it on blur — for an open vocabulary
 * (concepts) where a value not yet in the list is still a valid thing to type, as
 * opposed to a closed one (stacks) where only an existing option is ever acceptable.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  className,
  allowCustomValue = false,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  className?: string;
  allowCustomValue?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  function commit(option: ComboboxOption) {
    onChange(option.value);
    setQuery("");
    setOpen(false);
  }

  function commitCustom() {
    const trimmed = query.trim();
    if (allowCustomValue && trimmed) onChange(trimmed);
    setQuery("");
    setOpen(false);
  }

  const listboxId = id ? `${id}-listbox` : undefined;

  return (
    <div className={cn("relative", className)}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[highlight] ? `${listboxId}-${highlight}` : undefined}
        autoComplete="off"
        className={cn(BASE, "h-8 pr-7")}
        value={open ? query : selectedLabel}
        placeholder={selectedLabel}
        onFocus={() => {
          setQuery("");
          const i = options.findIndex((o) => o.value === value);
          setHighlight(i === -1 ? 0 : i);
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
        }}
        onBlur={commitCustom}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) setOpen(true);
            else setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            if (open && filtered[highlight]) {
              e.preventDefault();
              commit(filtered[highlight]);
            } else if (open && allowCustomValue && query.trim()) {
              e.preventDefault();
              commitCustom();
            }
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      <ChevronDown
        className="text-bank-muted pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2"
        aria-hidden="true"
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className="bg-bank-surface border-bank-border-strong absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border py-1 shadow-md"
        >
          {filtered.length === 0 ? (
            <li className="text-bank-muted px-2.5 py-1.5 text-sm">No matches</li>
          ) : (
            filtered.map((option, i) => (
              <li
                key={option.value}
                id={listboxId ? `${listboxId}-${i}` : undefined}
                role="option"
                aria-selected={option.value === value}
                ref={i === highlight ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined}
                onClick={() => commit(option)}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  "cursor-pointer px-2.5 py-1.5 text-sm",
                  i === highlight && "bg-bank-accent-wash text-bank-accent-ink",
                )}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
